export interface ParseAppOption {
  /**
   * Back4App internal id (dashboard UUID).
   * This is the id used by parseAppInfo and setAgentCurrentParseApp.
   */
  dashboardId: string;
  appName: string;
  /**
   * Parse Application ID (X-Parse-Application-Id) when present in the raw payload.
   */
  appId?: string;
}

// Back4App internal Parse App ids appear as UUIDs in most dashboards, but some environments can
// expose 24-hex ids (Mongo ObjectId) or embed the id inside URLs like /apps/{id}/overview.
const DASHBOARD_ID_RE =
  /(?:[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}|[a-f0-9]{24})/i;

function tryParseJsonString(value: unknown): any | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return undefined;
  try {
    return JSON.parse(trimmed);
  } catch {
    return undefined;
  }
}

function extractDashboardIdFromUnknown(value: unknown): string | undefined {
  if (!value) return undefined;
  const s = String(value);
  // Prefer extracting ids from known URL shapes first
  const fromUrl = s.match(/\/(?:apps|parse-app)\/([^/?#]+)/i)?.[1];
  if (fromUrl && DASHBOARD_ID_RE.test(fromUrl)) {
    return fromUrl.match(DASHBOARD_ID_RE)?.[0];
  }

  const match = s.match(DASHBOARD_ID_RE);
  return match?.[0];
}

function extractDashboardIdFromObjectShallow(obj: any): string | undefined {
  if (!obj || typeof obj !== 'object') return undefined;
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    if (typeof value === 'string') {
      const id = extractDashboardIdFromUnknown(value);
      if (id) return id;
    }
  }
  return undefined;
}

/**
 * Back4App `/listApps` payload is heterogeneous.
 * This helper extracts Parse App-like entries into a stable list usable by the dashboard UI.
 */
export function extractParseAppOptions(raw: any[]): ParseAppOption[] {
  if (!Array.isArray(raw)) return [];

  const out: ParseAppOption[] = [];

  for (const item of raw) {
    if (!item) continue;

    // Back4App GraphQL scalar Unknown may serialize objects as JSON strings.
    const parsedFromString = tryParseJsonString(item);
    const anyItem: any =
      parsedFromString && typeof parsedFromString === 'object'
        ? parsedFromString
        : item;

    if (!anyItem || typeof anyItem !== 'object') continue;

    const dashboardId =
      extractDashboardIdFromUnknown(anyItem.id) ||
      extractDashboardIdFromUnknown(anyItem._id) ||
      extractDashboardIdFromUnknown(anyItem.dashboardId) ||
      extractDashboardIdFromUnknown(anyItem.dashboardURL) ||
      extractDashboardIdFromUnknown(anyItem.dashboardUrl) ||
      extractDashboardIdFromUnknown(anyItem.url) ||
      extractDashboardIdFromUnknown(anyItem.href) ||
      extractDashboardIdFromUnknown(anyItem.link) ||
      extractDashboardIdFromObjectShallow(anyItem);

    const appName =
      (typeof anyItem.appName === 'string' && anyItem.appName.trim()) ||
      (typeof anyItem.name === 'string' && anyItem.name.trim()) ||
      // Some payloads may only contain ids/keys; fall back to a friendly placeholder.
      undefined;

    if (!dashboardId) continue;

    const appId =
      (typeof anyItem.appId === 'string' && anyItem.appId.trim()) ||
      (typeof anyItem.applicationId === 'string' && anyItem.applicationId.trim()) ||
      undefined;

    out.push({
      dashboardId,
      appName: appName || `Parse App (${dashboardId.slice(0, 8)}…)`,
      appId,
    });
  }

  // Stable ordering
  out.sort((a, b) => a.appName.localeCompare(b.appName));
  return out;
}

